/**
 * The pure, tested surface for the web privacy card and blocked list. Every
 * fan-facing string on both screens lives here. Straight apostrophes are
 * deliberate and match the design frame. No component types a copy string
 * inline.
 */

export const PRIVACY_CARD_TITLE = "Privacy";
export const DISCOVERABLE_LABEL = "Let other fans find me";
export const DISCOVERABLE_DESCRIPTION =
  "When this is off, you don't show up in a search for your name or username.";
export const POOL_LIST_LABEL = "Show my pools on my profile";
export const POOL_LIST_DESCRIPTION =
  "When this is off, your pool list stays off your profile.";
export const BLOCKED_LIST_LABEL = "Blocked list";
export const PRIVACY_CONTEXT_POOLS =
  "People in your pools can always see your name and your locked picks.";
export const PRIVACY_CONTEXT_MESSAGES =
  "Anyone who messages you privately for the first time has to be approved by you, even people in your pools.";
export const BLOCKED_HEADING = "Blocked list";
export const BLOCKED_EMPTY_HEADLINE = "You haven't blocked anyone.";
export const BLOCKED_EMPTY_BODY = "When you block someone, they show up here.";
export const BLOCKED_CONTEXT =
  "You can't see each other anywhere, not in profiles, pools, or messages.";
export const UNBLOCK_LABEL = "Unblock";
export const UNBLOCK_PENDING_LABEL = "Unblocking";
export const UNBLOCK_CONFIRM_BODY =
  "They'll be able to find you, message you, and invite you again.";
export const BLOCKED_LIST_ROUTE = "/settings/blocked";

export type typeBlockedRow = {
  accountId: string;
  displayName: string;
  /** `@username`, or an empty string when the username is blank. Never a bare "@". */
  handle: string;
  photoUrl: string | null;
  /** Up to two uppercase letters from displayName, for the avatar fallback. */
  initials: string;
};

/**
 * Splits `displayName` on whitespace and takes an initial from the first and
 * last parts. One part gives one letter. No parts gives `"?"`.
 */
export function resolveInitials(input: { displayName: string }): string {
  const parts = input.displayName.split(/\s+/).filter((part) => part !== "");
  const firstPart = parts.at(0);
  if (firstPart === undefined) return "?";
  const first = firstPart.charAt(0);
  if (parts.length === 1) return first.toUpperCase();
  const lastPart = parts.at(-1) ?? firstPart;
  const last = lastPart.charAt(0);
  return `${first}${last}`.toUpperCase();
}

export function toBlockedRow(input: {
  accountId: string;
  username: string;
  displayName: string;
  photoUrl?: string | null;
}): typeBlockedRow {
  const trimmedUsername = input.username.trim();
  return {
    accountId: input.accountId,
    displayName: input.displayName,
    handle: trimmedUsername === "" ? "" : `@${trimmedUsername}`,
    photoUrl: input.photoUrl ?? null,
    initials: resolveInitials({ displayName: input.displayName }),
  };
}

export function unblockConfirmTitle(displayName: string): string {
  return `Unblock ${displayName}?`;
}

/**
 * How many blocked rows both web privacy surfaces ask `block/list` for.
 *
 * `block/list` became cursor paged in ISSUE-376 and answers with 50 rows when
 * the caller sends no limit (`BLOCK_LIST_DEFAULT_LIMIT`,
 * `services/profile-api/src/repo/blocks.ts`). The handler clamps whatever it is
 * given down to `BLOCK_LIST_MAX` (`postBlockListHandler/index.ts:70`), and
 * `postBlockAddHandler/index.ts:65` refuses a new block once the fan already
 * holds `BLOCK_LIST_MAX` (`services/profile-api/src/models/blocks.ts:12`, value
 * 500). So the cap on how many blocks can exist and the ceiling on one page are
 * the same number, which makes a page this size the whole roster by
 * construction. That equality is an invariant this module's own test checks
 * rather than a coincidence, so raising the server cap reddens the build here.
 */
export const BLOCKED_LIST_PAGE_LIMIT = 500;

export const BLOCKED_TRUNCATED_NOTICE =
  "You have blocked more people than this list can show. Unblock someone to see the rest.";

/**
 * The number beside the blocked list link. `count` is `items.length` from one
 * `block/list` page, so it is a total only when that page proved itself
 * complete with a null `nextCursor`. The `+` form exists so this card can never
 * present a page size as a total.
 */
export function blockedCountLabel(input: {
  count: number;
  isComplete: boolean;
}): string {
  if (!input.isComplete) return `${input.count}+`;
  return `${input.count}`;
}
